import type { AgentWeekInput } from "./shared";

export type ValidationDetails = {
  field?: string;
  [key: string]: unknown;
};

/** A stable, JSON-safe error shared by the Worker and browser validation. */
export class ValidationError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: ValidationDetails;

  constructor(status: number, code: string, message: string, details: ValidationDetails = {}) {
    super(message);
    this.name = "ValidationError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export type NormalizedWeekInput = AgentWeekInput & {
  expected_revision?: number;
};

export type JsonRecord = Record<string, unknown>;

const MAX_BODY_BYTES = 256_000;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const HTTP_URL_RE = /^https?:$/i;

function invalid(field: string, message: string, code = "invalid_field"): never {
  throw new ValidationError(400, code, message, { field });
}

function requiredObject(value: unknown, field: string): JsonRecord {
  if (!value || typeof value !== "object" || Array.isArray(value)) invalid(field, `${field} must be an object`);
  return value as JsonRecord;
}

function optional(value: unknown): boolean {
  return value === undefined || value === null || value === "";
}

export function stringValue(value: unknown, field: string, options: { required?: boolean; max?: number } = {}): string | undefined {
  if (optional(value)) {
    if (options.required) invalid(field, `${field} is required`, "required");
    return undefined;
  }
  if (typeof value !== "string") invalid(field, `${field} must be a string`, "invalid_type");
  const result = (value as string).trim();
  if (options.required && !result) invalid(field, `${field} is required`, "required");
  if ([...result].length > (options.max ?? 2_000)) invalid(field, `${field} is too long`, "too_long");
  return result;
}

export function stringArray(value: unknown, field: string, options: { maxItems?: number; maxLength?: number } = {}): string[] {
  if (!Array.isArray(value)) invalid(field, `${field} must be an array`, "invalid_type");
  if (value.length > (options.maxItems ?? 30)) invalid(field, `${field} has too many items`, "too_many_items");
  return value.map((item, index) => stringValue(item, `${field}[${index}]`, { required: true, max: options.maxLength ?? 500 })!);
}

export function numberValue(value: unknown, field: string, options: { required?: boolean; min?: number; max?: number; integer?: boolean } = {}): number | undefined {
  if (optional(value)) {
    if (options.required) invalid(field, `${field} is required`, "required");
    return undefined;
  }
  if (typeof value !== "number") invalid(field, `${field} must be a number`, "invalid_type");
  const result = value as number;
  if (!Number.isFinite(result)) invalid(field, `${field} must be a number`, "invalid_number");
  if (options.integer && !Number.isInteger(result)) invalid(field, `${field} must be an integer`, "invalid_integer");
  if (options.min !== undefined && result < options.min) invalid(field, `${field} must be at least ${options.min}`, "out_of_range");
  if (options.max !== undefined && result > options.max) invalid(field, `${field} must be at most ${options.max}`, "out_of_range");
  return result;
}

function dateParts(value: string, field: string): [number, number, number] {
  if (!DATE_RE.test(value)) invalid(field, `${field} must be YYYY-MM-DD`, "invalid_date");
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const date = new Date(0);
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCFullYear(year, month - 1, day);
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    invalid(field, `${field} must be a real calendar date`, "invalid_date");
  }
  return [year, month, day];
}

function dateOrdinal(value: string): number {
  const [year, month, day] = dateParts(value, "date");
  const date = new Date(0);
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCFullYear(year, month - 1, day);
  return date.getTime();
}

function validateDate(value: unknown, field: string): string {
  const result = stringValue(value, field, { required: true, max: 10 })!;
  dateParts(result, field);
  return result;
}

function validateUrl(value: unknown, field: string): string | undefined {
  const result = stringValue(value, field, { max: 1_000 });
  if (result === undefined) return undefined;
  try {
    const url = new URL(result);
    if (!HTTP_URL_RE.test(url.protocol)) invalid(field, `${field} must be an http(s) URL`, "invalid_url");
  } catch {
    invalid(field, `${field} must be an http(s) URL`, "invalid_url");
  }
  return result;
}

function validateCues(value: unknown, field: string): string[] | undefined {
  if (value === undefined) return undefined;
  return stringArray(value, field, { maxItems: 8, maxLength: 240 });
}

function sentenceCount(value: string): number {
  const terminal = value.match(/[.!?]+(?=\s|$)/g);
  return terminal?.length || 1;
}

export function validateBanner(value: unknown, field = "coach_notes", required = false): string | undefined {
  const result = stringValue(value, field, { required, max: 300 });
  if (result === undefined) return undefined;
  const words = result.match(/\S+/g)?.length ?? 0;
  if (words > 40) invalid(field, `${field} must contain at most 40 words`, "banner_too_many_words");
  if (sentenceCount(result) > 3) invalid(field, `${field} must contain at most 3 sentences`, "banner_too_many_sentences");
  return result;
}

export function validateWeek(body: JsonRecord): NormalizedWeekInput {
  const weekStart = validateDate(body.week_start, "week_start");
  if (new Date(dateOrdinal(weekStart)).getUTCDay() !== 1) invalid("week_start", "week_start must be a Monday", "invalid_week_start");
  const title = stringValue(body.title, "title", { required: true, max: 120 })!;
  if (!Array.isArray(body.days) || body.days.length < 1 || body.days.length > 7) invalid("days", "days must contain 1 to 7 workout days", "invalid_count");

  const weekStartOrdinal = dateOrdinal(weekStart);
  const seenDates = new Set<string>();
  let totalExerciseCount = 0;
  let previousDayOrdinal = -Infinity;
  const days = body.days.map((rawDay, dayPosition) => {
    const dayField = `days[${dayPosition}]`;
    const day = requiredObject(rawDay, dayField);
    const dayDate = validateDate(day.day_date, `${dayField}.day_date`);
    if (seenDates.has(dayDate)) invalid(`${dayField}.day_date`, "day dates must be unique", "duplicate_date");
    seenDates.add(dayDate);
    const offset = (dateOrdinal(dayDate) - weekStartOrdinal) / 86_400_000;
    if (offset < 0 || offset > 6) invalid(`${dayField}.day_date`, "day_date must fall within week_start and the following Sunday", "date_outside_week");
    const dayOrdinal = dateOrdinal(dayDate);
    if (dayOrdinal < previousDayOrdinal) invalid(`${dayField}.day_date`, "days must be in ascending date order", "invalid_order");
    previousDayOrdinal = dayOrdinal;
    if (!Array.isArray(day.blocks) || day.blocks.length < 1 || day.blocks.length > 12) invalid(`${dayField}.blocks`, "blocks must contain 1 to 12 blocks", "invalid_count");

    return {
      day_date: dayDate,
      title: stringValue(day.title, `${dayField}.title`, { required: true, max: 120 })!,
      focus: stringValue(day.focus, `${dayField}.focus`, { max: 500 }),
      estimated_minutes: numberValue(day.estimated_minutes, `${dayField}.estimated_minutes`, { integer: true, min: 5, max: 300 }),
      blocks: day.blocks.map((rawBlock, blockPosition) => {
        const blockField = `${dayField}.blocks[${blockPosition}]`;
        const block = requiredObject(rawBlock, blockField);
        const kindValue = stringValue(block.kind, `${blockField}.kind`, { max: 20 }) ?? "superset";
        if (!(kindValue === "superset" || kindValue === "straight" || kindValue === "circuit")) invalid(`${blockField}.kind`, "block.kind is invalid", "invalid_enum");
        if (!Array.isArray(block.exercises)) invalid(`${blockField}.exercises`, "exercises must be an array", "invalid_type");
        const blockExerciseCount = block.exercises.length;
        const validCount = kindValue === "superset" ? blockExerciseCount === 2 : kindValue === "straight" ? blockExerciseCount === 1 : blockExerciseCount >= 2 && blockExerciseCount <= 6;
        if (!validCount) invalid(`${blockField}.exercises`, `${kindValue} blocks require ${kindValue === "circuit" ? "2 to 6" : kindValue === "superset" ? "2" : "1"} exercises`, "invalid_count");
        totalExerciseCount += blockExerciseCount;
        if (totalExerciseCount > 200) invalid("days", "week cannot contain more than 200 exercise entries", "plan_too_large");
        const rounds = numberValue(block.rounds, `${blockField}.rounds`, { required: true, integer: true, min: 1, max: 12 })!;
        return {
          label: stringValue(block.label, `${blockField}.label`, { max: 20 }),
          kind: kindValue as "superset" | "circuit" | "straight",
          rounds,
          rest_seconds: numberValue(block.rest_seconds, `${blockField}.rest_seconds`, { integer: true, min: 0, max: 900 }),
          notes: stringValue(block.notes, `${blockField}.notes`, { max: 1_000 }),
          exercises: block.exercises.map((rawExercise, exercisePosition) => {
            const exerciseField = `${blockField}.exercises[${exercisePosition}]`;
            const exercise = requiredObject(rawExercise, exerciseField);
            const durationSeconds = numberValue(exercise.duration_seconds, `${exerciseField}.duration_seconds`, { integer: true, min: 1, max: 3_600 });
            const repMin = numberValue(exercise.rep_min, `${exerciseField}.rep_min`, { integer: true, min: 1, max: 1_000 });
            const repMax = numberValue(exercise.rep_max, `${exerciseField}.rep_max`, { integer: true, min: 1, max: 1_000 });
            if (durationSeconds !== undefined && (repMin !== undefined || repMax !== undefined)) invalid(exerciseField, "Use duration_seconds or rep_min/rep_max, not both", "targets_mutually_exclusive");
            if (durationSeconds === undefined && repMin === undefined && repMax === undefined) invalid(exerciseField, "Provide rep_min/rep_max or duration_seconds", "target_required");
            if (repMin !== undefined && repMax !== undefined && repMin > repMax) invalid(`${exerciseField}.rep_min`, "rep_min must be at most rep_max", "invalid_order");
            const targetSets = numberValue(exercise.target_sets, `${exerciseField}.target_sets`, { integer: true, min: 1, max: 12 });
            if (targetSets !== undefined && targetSets !== rounds) invalid(`${exerciseField}.target_sets`, "target_sets must equal block.rounds", "inconsistent_sets");
            return {
              name: stringValue(exercise.name, `${exerciseField}.name`, { required: true, max: 120 })!,
              equipment: stringValue(exercise.equipment, `${exerciseField}.equipment`, { max: 120 }),
              video_url: validateUrl(exercise.video_url, `${exerciseField}.video_url`),
              cues: validateCues(exercise.cues, `${exerciseField}.cues`),
              target_sets: targetSets ?? rounds,
              rep_min: repMin,
              rep_max: repMax,
              duration_seconds: durationSeconds,
              target_rir: numberValue(exercise.target_rir, `${exerciseField}.target_rir`, { min: 0, max: 10 }),
              target_weight: numberValue(exercise.target_weight, `${exerciseField}.target_weight`, { min: 0, max: 10_000 }),
              notes: stringValue(exercise.notes, `${exerciseField}.notes`, { max: 1_000 })
            };
          })
        };
      })
    };
  });

  const expectedRevision = numberValue(body.expected_revision, "expected_revision", { integer: true, min: 0, max: Number.MAX_SAFE_INTEGER });
  return {
    week_start: weekStart,
    title,
    focus: stringValue(body.focus, "focus", { max: 500 }),
    coach_notes: validateBanner(body.coach_notes, "coach_notes"),
    warmup: body.warmup === undefined ? undefined : stringArray(body.warmup, "warmup", { maxItems: 12, maxLength: 240 }),
    days,
    ...(expectedRevision === undefined ? {} : { expected_revision: expectedRevision })
  };
}

export async function parseJson(request: Request): Promise<JsonRecord> {
  const declared = request.headers.get("content-length");
  if (declared !== null && /^\d+$/.test(declared) && Number(declared) > MAX_BODY_BYTES) {
    throw new ValidationError(413, "request_too_large", "Request body is too large", { field: "body" });
  }
  try {
    const reader = request.body?.getReader();
    if (!reader) throw new Error("body required");
    const chunks: Uint8Array[] = [];
    let size = 0;
    while (true) {
      const part = await reader.read();
      if (part.done) break;
      size += part.value.byteLength;
      if (size > MAX_BODY_BYTES) {
        await reader.cancel();
        throw new ValidationError(413, "request_too_large", "Request body is too large", { field: "body" });
      }
      chunks.push(part.value);
    }
    const bytes = new Uint8Array(size);
    let offset = 0;
    for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
    const value: unknown = JSON.parse(new TextDecoder().decode(bytes));
    if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("object required");
    return value as JsonRecord;
  } catch (error) {
    if (error instanceof ValidationError) throw error;
    throw new ValidationError(400, "invalid_json", "Expected a valid JSON object", { field: "body" });
  }
}
